import { createPortal } from "react-dom";
import React, {
  CSSProperties,
  MouseEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import { CloseIcon } from "assets/svgs";
import { useAppSelector } from "store/hooks";
import { userSettingState } from "store/selectors";
import styles from "./style.module.css";

function ModalContent({
  onClose,
  children,
  isOpen,
  classes,
  isDraggable,
}: any) {
  const [isVisible, setIsVisible] = useState(false);
  const { isDarkMode } = useAppSelector(userSettingState);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isVisible) return null;

  return (
    <div
      className={`${styles.customModal} ${
        isDarkMode ? styles.customModalDark : ""
      }  ${isOpen ? "opacity-100" : "opacity-0"}`}
    >
      <ModalContentWrapper
        isDraggable={isDraggable}
        classes={classes}
        isOpen={isOpen}
      >
        <button className={styles.closeBtn} onClick={onClose}>
          <CloseIcon />
        </button>
        {children}
      </ModalContentWrapper>
    </div>
  );
}

export function CustomModal({
  onClose,
  children,
  isOpen,
  classes,
  isDraggable = true,
}: any) {
  return createPortal(
    <ModalContent
      onClose={onClose}
      isOpen={isOpen}
      classes={classes}
      isDraggable={isDraggable}
    >
      {children}
    </ModalContent>,
    document.body
  );
}

function ModalContentWrapper({
  isDraggable,
  children,
  isOpen,
  classes,
}: {
  isDraggable: boolean;
  children: React.ReactNode;
  isOpen: boolean;
  classes: string;
}) {
  const [position, setPosition] = useState<null | { x: number; y: number }>(
    null
  );
  const dragging = useRef<boolean>(false);
  const offset = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const modalRef = useRef<HTMLDivElement | null>(null);
  const { isDarkMode } = useAppSelector(userSettingState);

  useEffect(() => {
    const handleMouseMove = (e: any) => {
      if (!dragging.current) return;
      setPosition({
        x: e.clientX - offset.current.x,
        y: e.clientY - offset.current.y,
      });
    };

    const handleMouseUp = () => {
      dragging.current = false;
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const handleMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    if (!modalRef.current) return;

    dragging.current = true;
    const rect = modalRef.current.getBoundingClientRect();
    offset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const modalStyles = position
    ? {
        position: "absolute",
        left: position.x,
        top: position.y,
        cursor: "grab",
        userSelect: "none",
        transition: "all 0.3s linear",
      }
    : {};

  if (isDraggable) {
    return (
      <div
        ref={modalRef}
        className={`${styles.modalContent} ${
          isDarkMode ? styles.darkMode : ""
        } ${classes} ${isOpen ? "scale-100" : "scale-80"}`}
        style={modalStyles as CSSProperties}
        onMouseDown={handleMouseDown}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      className={`${styles.modalContent} ${
        isDarkMode ? styles.darkMode : ""
      } ${classes} ${isOpen ? "scale-100" : "scale-80"}`}
    >
      {children}
    </div>
  );
}

function Header({ title }: any) {
  return (
    <div className={styles.header}>
      <h2>{title}</h2>
    </div>
  );
}

function Body({ children, classes }: any) {
  return <div className={`${styles.body} ${classes}`}>{children}</div>;
}

function Footer({ children, classes }: any) {
  return <div className={`${styles.footer} ${classes}`}>{children}</div>;
}

export const Modal = {
  Header,
  Body,
  Footer,
};
